// src/components/Cards/LayerSelectionCard.tsx
import React, { useState } from 'react';
import { Box, Card, CardContent, Grid, Typography, IconButton, Badge, Tooltip } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import { useGetLayerImage } from '../../Hooks/useGetLayerImage';
import LayerInfo from '../Dialog/LayerInfo';
import { useTheme } from '@mui/material/styles';
import useTranslation from '../Language/useTranslation';
import ndvi_calculated from '../../Images/Thumbnails/ndvi_calculated.png';

interface LayerSelectionCardProps {
    datasetId?: string;
    selectedLayer: string | null;
    setSelectedLayer: React.Dispatch<React.SetStateAction<string | null>>;
    layerType?: string;
    imgLink?: string;
    layerDescription?: string | null;
    name?: string;
    isNew?: boolean;
    withDate?: boolean;
    date?: string;
}

const LayerImage: React.FC<{ imgSrc: string; isNew: boolean }> = ({ imgSrc, isNew }) => {
    const t = useTranslation();
    return (
        <Badge
            color="primary"
            badgeContent={t.extras.badge}
            invisible={!isNew}
            sx={{ '& .MuiBadge-badge': { fontSize: '0.6rem', minWidth: '12px', height: '16px', padding: '0 4px', transform: 'translate(18px, -10px)' } }}
        >
            <Box component="img" sx={{ width: 40, height: 40, borderRadius: 1 }} src={imgSrc} />
        </Badge>
    );
};

const LayerDetails: React.FC<{ name?: string; withDate?: boolean; date?: string; isSelected: boolean }> = ({ name, withDate, date, isSelected }) => {
    const theme = useTheme();
    const formatDate = (dateString: string) => new Date(dateString).toLocaleDateString('de-DE');
    const truncatedName = name ? (name.length >= 20 ? name.substring(0, 17) + "..." : name) : "unlabeled";

    return (
        <>
            {withDate ? (
                <Tooltip title={name || ''} disableHoverListener={!name || name.length < 20}>
                    <Typography
                        variant="subtitle1"
                        color="text.secondary"
                        sx={{ color: isSelected ? theme.palette.primary.main : undefined }}
                    >
                        {truncatedName}
                    </Typography>
                </Tooltip>
            ) : (
                <Typography
                    variant="subtitle1"
                    color="text.secondary"
                    sx={{ color: isSelected ? theme.palette.primary.main : undefined }}
                >
                    {name ? (name.length >= 30 ? name.substring(0, 27) + "..." : name) : "unlabeled"}
                </Typography>
            )}
            {withDate && date && (
                <Typography variant="caption" color="text.secondary" sx={{ color: isSelected ? theme.palette.primary.main : undefined }}>
                    {formatDate(date)}
                </Typography>
            )}
        </>
    );
};

const LayerSelectionCard: React.FC<LayerSelectionCardProps> = ({
    datasetId,
    selectedLayer,
    setSelectedLayer,
    imgLink,
    layerDescription,
    name,
    isNew = false,
    withDate = false,
    date
}) => {
    const [showInfo, setShowInfo] = useState(false);
    const theme = useTheme();
    const fetchedImageSrc = useGetLayerImage(imgLink);
    const imageSrc = datasetId === "NDVI_CHANGE_SINCE_PROJECT_START" ? ndvi_calculated : fetchedImageSrc;

    const handleClickLayer = () => {
        if (!showInfo) setSelectedLayer(datasetId === undefined || selectedLayer === datasetId ? null : datasetId);
    };

    return (
        <>
            {showInfo && <LayerInfo setShowInfo={setShowInfo} name={name} layerDescription={layerDescription} />}
            <Card
                onClick={handleClickLayer}
                sx={{ border: 1, borderColor: selectedLayer === datasetId ? theme.palette.primary.main : 'transparent', '&:hover': { cursor: 'pointer', borderColor: theme.palette.primary.main, boxShadow: 3 } }}
            >
                <CardContent sx={{ padding: 1.5, '&:last-child': { paddingBottom: 1.5 } }}>
                    <Grid container justifyContent="space-between" alignItems="center" direction="row" height="50px" p={0}>
                        <Grid item sm={3}>
                            <LayerImage imgSrc={imageSrc} isNew={isNew} />
                        </Grid>
                        <Grid item sm={9} container alignItems="center" direction="row">
                            <Grid item sm={10}>
                                <LayerDetails name={name} withDate={withDate} date={date} isSelected={selectedLayer === datasetId} />
                            </Grid>
                            <Grid item sm={2} container justifyContent="flex-end">
                                <IconButton sx={{ padding: 0 }} onClick={(e) => { setShowInfo(true); e.stopPropagation(); }}>
                                    <InfoIcon />
                                </IconButton>
                            </Grid>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>
        </>
    );
};

export default LayerSelectionCard;
